<!--
/**
 * @copyright   OnlyOneProd © 2025
 * @authors     Jonathan COULLET <contact@onlyoneprod.com>
 * @link        https://onlyoneprod.com
 * @notice      All information contained herein is,
 *              and remains the property of OnlyOneProd and its suppliers,
 *              Any dissemination of this information or reproduction
 *              of this material is strictly forbidden unless prior
 *              written permission is obtained from OnlyOneProd.
 */
-->

<template>
    <div class="shape-animation">
        <div class="shape-container" :class="shapePosition">
            <svg xmlns="http://www.w3.org/2000/svg"
                :viewBox="`${-shapeWidth} ${-shapeWidth} ${shapeWidth} ${shapeWidth}`" ref="svg" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, defineProps, onMounted, ref } from "vue";

const svg = ref<SVGSVGElement | null>(null);
const props = defineProps<{
    shape?: "circle" | "polygon";
    sides?: number;
    rotation?: number;
    duration?: number;
    position?: "left" | "right";
}>();

const shapeWidth = 1000;
const motionSpline = ".25 .75 .75 .25";

const shape = computed(() => props.shape || "circle");
const duration = computed(() => props.duration || 3);
const decay = computed(() => duration.value / 8);
const shapePosition = computed(() => {
    if (props.position == undefined) {
        return "";
    }
    return props.position;
});

const degreeToRadian = (deg: number) => deg * Math.PI / 180;

onMounted(() => {
    if (svg.value) {
        const svgElement = svg.value;
        const svgNS = svgElement.namespaceURI;
        const radius = shapeWidth / 20;
        let i;

        if (shape.value == "circle") {
            const circle = document.createElementNS(svgNS, "circle") as SVGCircleElement;
            circle.setAttribute("cx", "0");
            circle.setAttribute("cy", "0");
            circle.setAttribute("r", radius.toString());
            circle.setAttribute("opacity", "0");

            for (i = 0; i < 4; i++) {
                let _circle = circle.cloneNode(true) as SVGCircleElement;
                _circle.innerHTML = `
                    <animate attributeName="r" values="${radius};${radius * 5};${radius * 15}" calcMode="spline" keyTimes="0;.5;1" keySplines="${motionSpline};${motionSpline}" dur="${duration.value}s" begin="${i * decay.value}s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0;1;.75;.5;0" dur="${duration.value}s" begin="${i * decay.value}s" repeatCount="indefinite" />
                `;
                svgElement.appendChild(_circle);
            }

        } else {
            const sides = props.sides || 3;
            const points = [];
            const angle = Math.PI * 2 / sides;

            for (i = 0; i < sides; i++) {
                const rotatedAngle = angle * i + (props.rotation ? degreeToRadian(props.rotation) : 0);
                const x = radius * Math.cos(rotatedAngle);
                const y = radius * Math.sin(rotatedAngle);
                points.push(`${x},${y}`);
            }

            const polygon = document.createElementNS(svgNS, "polygon") as SVGPolygonElement;
            polygon.setAttribute("points", points.join(" "));
            polygon.setAttribute("opacity", "0");

            for (i = 0; i < 4; i++) {
                let _polygon = polygon.cloneNode(true) as SVGPolygonElement;
                _polygon.innerHTML = `
                    <animateTransform attributeName="transform" type="scale" from="1" to="20" calcMode="spline" keyTimes="0;1" keySplines="${motionSpline}" dur="${duration.value}s" begin="${i * decay.value}s" repeatCount="indefinite" />
                    <animate attributeName="stroke-width" from=".5" to=".05" calcMode="spline" keyTimes="0;1" keySplines="${motionSpline}" dur="${duration.value}s" begin="${i * decay.value}s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0;1;.75;.5;0" dur="${duration.value}s" begin="${i * decay.value}s" repeatCount="indefinite" />
                `;
                svgElement.appendChild(_polygon);
            }
        }

    }
});
</script>

<style lang="sass">
.shape-animation
    @apply overflow-hidden

    .shape-container
        &.left
            @apply -translate-x-36 md:-translate-x-96

        &.right
            @apply translate-x-36 md:translate-x-96

    svg
        @apply text-current overflow-visible opacity-50 h-screen -translate-x-1/2 -translate-y-1/2

        circle, polygon
            @apply fill-none stroke-current stroke-2
</style>